import {type InfiniteData, keepPreviousData, useInfiniteQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import {DEFAULT_PROJECT_SORT, type ProjectSortValue} from './project-sort-options'
import type {ProjectsResponse} from './types'

/**
 * Leading segments of the queryKey used by `useProjects`. Exported so cache-reader
 * hooks (e.g. `useShowTemplatesIndex`) can match against this hook's entries with
 * `getQueriesData({queryKey: [...PROJECTS_QUERY_KEY_PREFIX, owner, repo]})` without
 * silently breaking when the full queryKey shape changes.
 */
export const PROJECTS_QUERY_KEY_PREFIX = ['repo-projects'] as const

interface UseProjectsOptions {
  owner: string
  repo: string
  state: 'open' | 'closed'
  query?: string
  sort?: ProjectSortValue
  isTemplateView?: boolean
}

interface PageParam {
  cursor: string | null
  sortQueryCursor: string | null
}

const INITIAL_PAGE_PARAM: PageParam = {cursor: null, sortQueryCursor: null}

export function useProjects({
  owner,
  repo,
  state,
  query,
  sort = DEFAULT_PROJECT_SORT,
  isTemplateView = false,
}: UseProjectsOptions) {
  const trimmedQuery = query?.trim() ?? ''
  return useInfiniteQuery<ProjectsResponse, Error, InfiniteData<ProjectsResponse>, unknown[], PageParam>({
    queryKey: [...PROJECTS_QUERY_KEY_PREFIX, owner, repo, state, trimmedQuery, sort, isTemplateView],
    initialPageParam: INITIAL_PAGE_PARAM,
    placeholderData: keepPreviousData,
    queryFn: async ({pageParam}) => {
      const baseQuery = state === 'closed' ? 'is:closed' : 'is:open'
      // The projects-list endpoint expects sort and template state as qualifiers inside
      // the `query` string. Omit defaults so URLs and requests stay clean.
      const sortQualifier = sort !== DEFAULT_PROJECT_SORT ? `sort:${sort}` : ''
      const templateQualifier = isTemplateView ? 'is:template' : ''
      const fullQuery = [baseQuery, trimmedQuery, sortQualifier, templateQualifier].filter(Boolean).join(' ')
      const params = new URLSearchParams({query: fullQuery})
      if (pageParam.cursor) {
        params.set('cursor', pageParam.cursor)
      }
      if (pageParam.sortQueryCursor) {
        params.set('sort_query_cursor', pageParam.sortQueryCursor)
      }
      const response = await verifiedFetchJSON(`/${owner}/${repo}/projects?${params.toString()}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`)
      }
      return (await response.json()) as ProjectsResponse
    },
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? {cursor: lastPage.cursor, sortQueryCursor: lastPage.sortQueryCursor} : undefined,
  })
}
